import styles from "./TeamSwitcher.module.css";
import { privateWorkspaceName, teamIconName } from "@app/config/constants";
import posthogEvents from "@app/config/posthog/posthog-events";
import { updateSelectedTeam } from "@app/state/actions";
import { useGlobalStore } from "@app/state/store";
import type { Team } from "@globals";
import Icon from "@lib/common/components/Icon/Icon";
import promiseDelay from "@lib/common/utils/promiseDelay";
import { getRoleDisplayName } from "@lib/iam/utils/getRoleDisplayName";
import invariant from "@lib/utils/invariant";
import { Avatar, Box, Card, Divider, Flex, Group, ScrollArea, Stack, Text, Title, UnstyledButton } from "@mantine/core";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import { useNavigate } from "react-router";

type Props = {
  onTeamSelection: (team: Team | null) => void;
};

export default function TeamSwitcher({ onTeamSelection }: Props) {
  const { user, selectedTeam } = useGlobalStore();

  invariant(user, "User must be defined to render TeamSwitcher");

  // use local state to display selected team immediately, but not update global until navigation is done
  const [uiSelectedTeam, setUiSelectedTeam] = useState(selectedTeam);

  const navigate = useNavigate();
  const posthog = usePostHog();

  const handleTeamSelect = async (team: Team | null) => {
    posthog.capture(posthogEvents.team_switcher.team_click, { team: team?.name ?? privateWorkspaceName });
    setUiSelectedTeam(team); // update local UI state immediately

    if (typeof onTeamSelection === "function") {
      onTeamSelection(team); // close modal
    }
    await promiseDelay(200); // match mantine modal close animation duration
    // Order here matters, we want to navigate first
    // to avoid the team switching back to the current object team due to request invalidation + middleware
    await navigate("/");
    await updateSelectedTeam(team); // update global state after navigation
  };

  const teams = [
    ...user.teams.map(teamMapping => ({
      key: teamMapping.team.id,
      team: teamMapping.team,
      name: teamMapping.team.name,
      role: getRoleDisplayName(teamMapping.role)
    })),
    {
      key: privateWorkspaceName,
      name: privateWorkspaceName,
      team: null,
      role: "Explore privately before sharing with a team"
    }
  ];

  const content =
    user.teams.length === 0 ? (
      <Stack gap={5}>
        <Text size={"sm"} fw={600}>
          You are not assigned to any teams.
        </Text>
        <Text size={"sm"}>
          Any experiment and dataset you add will be stored in your private workspace, visible only to you.
        </Text>
      </Stack>
    ) : (
      <Stack gap="lg" py="sm">
        <ScrollArea.Autosize mah="80vh">
          <Stack gap={"sm"}>
            {teams.map(item => {
              const isSelected = item.team?.id === uiSelectedTeam?.id;
              const isPrivateWorkspace = null === item.team;

              return (
                <Box key={item.key}>
                  {isPrivateWorkspace && <Divider mb={"sm"} />}
                  <UnstyledButton w={"100%"} onClick={() => handleTeamSelect(item.team)}>
                    <Card className={styles.cardRoot} p={"xs"} data-selected={isSelected || undefined}>
                      <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                          <Avatar color={isPrivateWorkspace ? "gray" : "initials"} name={item.name} radius="md">
                            {isPrivateWorkspace ? (
                              <Icon name="lock" color="green" variant="solid" />
                            ) : (
                              <Icon name={teamIconName} variant="light" />
                            )}
                          </Avatar>
                          <Stack gap={0}>
                            <Text fw={600} truncate maw={290}>
                              {item.name}
                            </Text>
                            <Text size="sm" c="dimmed">
                              {item.role}
                            </Text>
                          </Stack>
                        </Group>
                        {isSelected && <Icon name={"check"} style={{ marginTop: 3 }} />}
                      </Group>
                    </Card>
                  </UnstyledButton>
                </Box>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>

        <Stack>
          <Group wrap="nowrap" align="center" gap="xs">
            <Flex>
              <Icon name="home" variant="light" color={"dimmed"} size="0.9em" />
            </Flex>
            <Text size={"xs"} c={"dimmed"}>
              Changing teams will redirect you to the <b>Home</b> page.
            </Text>
          </Group>
          <Group wrap="nowrap" align="flex-start" gap="xs">
            <Flex mt={2}>
              <Icon name="eye" variant="light" color={"dimmed"} size="0.9em" />
            </Flex>
            <Stack gap="xs">
              <Text size={"xs"} c={"dimmed"}>
                Everything that you create while on a team is accessibly to that team only.
              </Text>
              <Text size={"xs"} c={"dimmed"}>
                Your <b>Private</b> workspace is for your eyes only.
                <br />
                Experiments created in your Private workspace can be moved to a team later.
              </Text>
            </Stack>
          </Group>
        </Stack>
      </Stack>
    );

  return (
    <Stack gap={0}>
      <Group justify="space-between">
        <Title order={5}>Switch team</Title>
        <Text size="xs" fw={500} c="dimmed">
          {user.current_organization.name}
        </Text>
      </Group>
      {content}
    </Stack>
  );
}
